/**
 * Fail-closed CRM boundary for the WO-12 presentation-only Google Maps layer.
 *
 * This module never accepts pixels or a provider URL. It validates the exact
 * rights snapshot returned by the Designer Engine before the UI may construct
 * Google's official Maps JavaScript component.
 */

export const GOOGLE_MAPS_PRESENTATION_FLAG = 'googleMapsPresentation'
export const GOOGLE_MAPS_PRESENTATION_PURPOSE = 'presentation-display'
export const GOOGLE_MAPS_PRESENTATION_POLICY_PATH = '/api/v2/imagery/presentation-policy'
export const GOOGLE_MAPS_PRESENTATION_SOURCE_ID = 'google-maps-basemap'
export const GOOGLE_MAPS_PRESENTATION_SELECTOR = '[data-google-presentation-layer="true"]'

export const GOOGLE_MAPS_PRESENTATION_SURFACE_BY_IMAGERY_SURFACE = Object.freeze({
  designer: 'designer-presentation-view',
  studio: 'studio-presentation-view',
  studioProperty: 'studio-property-presentation-view',
  publicCalcPreview: 'public-calc-result-presentation-preview',
  kioskProperty: 'kiosk-property-presentation-view',
})
export const GOOGLE_MAPS_PROPERTY_PRESENTATION_SELECTOR_BY_IMAGERY_SURFACE = Object.freeze({
  studio: 'studioProperty',
  publicCalc: 'publicCalcPreview',
  kiosk: 'kioskProperty',
})
const EXACT_PRESENTATION_SURFACE_TAGS = Object.freeze(
  Object.values(GOOGLE_MAPS_PRESENTATION_SURFACE_BY_IMAGERY_SURFACE).sort()
)
const GOOGLE_PRESENTATION_MIN_REVIEW_DATE = '2026-07-18'
const GOOGLE_PRESENTATION_REQUIRED_EVIDENCE_URLS = Object.freeze(
  [
    'https://developers.google.com/maps/documentation/javascript/policies',
    'https://cloud.google.com/maps-platform/terms',
    'repo:gse/docs/architecture/DESIGNER_ENGINE_SPEC.md#presentation-layer-display-mode',
  ].sort()
)

const ALLOWED_CAPABILITIES = Object.freeze(['displayInteractive', 'commercialUse'])
const DENIED_CAPABILITIES = Object.freeze([
  'displayFlattened',
  'manualTrace',
  'serverDownload',
  'aiInference',
  'retainPixels',
  'retainDerivedGeometry',
  'customerFacingDerived',
])

function fail(message) {
  const error = new Error(message)
  error.code = 'invalid_google_presentation_policy'
  throw error
}

export function presentationSurfaceTagForImagerySurface(imagerySurface) {
  return GOOGLE_MAPS_PRESENTATION_SURFACE_BY_IMAGERY_SURFACE[imagerySurface] || null
}

export function propertyPresentationSelectorForImagerySurface(imagerySurface) {
  return GOOGLE_MAPS_PROPERTY_PRESENTATION_SELECTOR_BY_IMAGERY_SURFACE[imagerySurface] || null
}

export function isGoogleMapsPresentationFlagEnabled(value) {
  return value === true
}

/**
 * Request and validate one fresh, pixels-free policy snapshot.
 *
 * The caller owns any UI/controller generation checks around this asynchronous
 * boundary. Keeping request construction here prevents Tier-1 and Tier-2
 * surfaces from drifting on purpose or selector semantics.
 */
export async function requestGoogleMapsPresentationPolicy(requestPolicy, imagerySurface) {
  if (typeof requestPolicy !== 'function') fail('Presentation policy requester is unavailable.')
  const surfaceTag = presentationSurfaceTagForImagerySurface(imagerySurface)
  if (!surfaceTag) fail('This surface is not authorized for presentation mode.')
  const response = await requestPolicy(GOOGLE_MAPS_PRESENTATION_POLICY_PATH, {
    purpose: GOOGLE_MAPS_PRESENTATION_PURPOSE,
    surfaceTag,
  })
  return validateGoogleMapsPresentationPolicy(response, surfaceTag)
}

/**
 * Generation-safe admission coordinator shared by display-only property views.
 * Invalidating an in-flight request guarantees its late response cannot mount
 * a provider component.
 */
export function createGoogleMapsPresentationAdmission(requestPolicy) {
  let generation = 0
  return Object.freeze({
    invalidate() {
      generation += 1
    },
    async admit(imagerySurface, { canCommit = () => true } = {}) {
      const admissionGeneration = ++generation
      const policy = await requestGoogleMapsPresentationPolicy(requestPolicy, imagerySurface)
      if (admissionGeneration !== generation || canCommit() !== true) return null
      return policy
    },
  })
}

/**
 * Return the small, pixels-free subset the controller needs, or throw.
 * Unknown/missing capability fields are denied instead of inferred.
 */
export function validateGoogleMapsPresentationPolicy(value, expectedSurfaceTag) {
  if (!expectedSurfaceTag) fail('This Designer surface is not authorized for presentation mode.')
  if (!value || typeof value !== 'object') fail('Presentation policy response is missing.')
  if (value.schemaVersion !== 1) fail('Presentation policy schema is not supported.')
  if (value.purpose !== GOOGLE_MAPS_PRESENTATION_PURPOSE)
    fail('Presentation policy purpose does not match.')
  if (value.surfaceTag !== expectedSurfaceTag) fail('Presentation policy surface does not match.')

  const source = value.source
  if (!source || typeof source !== 'object') fail('Presentation source policy is missing.')
  if (source.sourceId !== GOOGLE_MAPS_PRESENTATION_SOURCE_ID)
    fail('Presentation source is not the approved Google Maps basemap.')
  if (source.delivery !== 'client-sdk') fail('Presentation source must use the client SDK.')
  if (source.maxPixelRetentionDays !== 0) fail('Presentation policy must forbid pixel retention.')
  if (!Array.isArray(source.allowedSurfaceTags) || source.allowedSurfaceTags.length !== 0)
    fail('Presentation policy contains an unexpected broad surface allowlist.')
  if (
    source.attributionRequired !== true ||
    !/google/i.test(String(source.attribution || '').trim())
  )
    fail('Presentation policy must require attribution.')
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(String(source.policyReviewedAt || '')) ||
    source.policyReviewedAt < GOOGLE_PRESENTATION_MIN_REVIEW_DATE
  )
    fail('Presentation policy evidence review is stale or missing.')
  const evidence = source.licenseEvidence
  if (
    !Array.isArray(evidence) ||
    evidence.length !== GOOGLE_PRESENTATION_REQUIRED_EVIDENCE_URLS.length ||
    JSON.stringify(evidence.map(item => item?.url).sort()) !==
      JSON.stringify(GOOGLE_PRESENTATION_REQUIRED_EVIDENCE_URLS) ||
    evidence.some(
      item =>
        !/^\d{4}-\d{2}-\d{2}$/.test(String(item?.reviewedAt || '')) ||
        item.reviewedAt < GOOGLE_PRESENTATION_MIN_REVIEW_DATE
    )
  )
    fail('Presentation policy license evidence is incomplete or stale.')
  if (!String(source.policySnapshotId || '').trim())
    fail('Presentation policy snapshot identity is missing.')
  if (
    !Array.isArray(source.allowedPurposes) ||
    source.allowedPurposes.length !== 1 ||
    source.allowedPurposes[0] !== GOOGLE_MAPS_PRESENTATION_PURPOSE
  )
    fail('Presentation purpose is not explicitly allowed.')
  const surfaceMap = source.allowedSurfaceTagsByPurpose
  if (
    !surfaceMap ||
    typeof surfaceMap !== 'object' ||
    Array.isArray(surfaceMap) ||
    Object.keys(surfaceMap).length !== 1 ||
    !Object.prototype.hasOwnProperty.call(surfaceMap, GOOGLE_MAPS_PRESENTATION_PURPOSE)
  )
    fail('Presentation surface policy is not exact.')
  const surfaces = surfaceMap[GOOGLE_MAPS_PRESENTATION_PURPOSE]
  if (
    !Array.isArray(surfaces) ||
    surfaces.length !== EXACT_PRESENTATION_SURFACE_TAGS.length ||
    JSON.stringify([...surfaces].sort()) !== JSON.stringify(EXACT_PRESENTATION_SURFACE_TAGS) ||
    !surfaces.includes(expectedSurfaceTag)
  )
    fail('Presentation surface is not explicitly allowed.')

  const capabilities = source.capabilities || {}
  const exactCapabilityNames = [...ALLOWED_CAPABILITIES, ...DENIED_CAPABILITIES].sort()
  if (JSON.stringify(Object.keys(capabilities).sort()) !== JSON.stringify(exactCapabilityNames))
    fail('Presentation capability policy is not exact.')
  ALLOWED_CAPABILITIES.forEach(name => {
    if (capabilities[name] !== 'allow') fail(`Presentation capability ${name} is not allowed.`)
  })
  DENIED_CAPABILITIES.forEach(name => {
    if (capabilities[name] !== 'deny') fail(`Presentation capability ${name} is not denied.`)
  })

  return Object.freeze({
    policySnapshotId: String(source.policySnapshotId),
    attribution: String(source.attribution),
    surfaceTag: expectedSurfaceTag,
  })
}

/** html2canvas and any future DOM capture must omit the official Google component. */
export function isGooglePresentationCaptureElement(element) {
  if (!element || typeof element !== 'object') return false
  if (element.dataset?.googlePresentationLayer === 'true') return true
  return typeof element.closest === 'function'
    ? !!element.closest(GOOGLE_MAPS_PRESENTATION_SELECTOR)
    : false
}
